import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import TableFilter from '../components/TableFilter';
import UserForm, { User } from '../components/UserForm';
import ActionButtons from '../components/ActionButtons';

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('users/');
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error("Error fetching users", error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleFilter = (filters: { [key: string]: string }) => {
    let result = users;

    if (filters.username) {
      result = result.filter(u => u.username.toLowerCase().includes(filters.username.toLowerCase()));
    }
    if (filters.name) {
      result = result.filter(u =>
        (u.first_name + ' ' + u.last_name).toLowerCase().includes(filters.name.toLowerCase())
      );
    }
    if (filters.email) {
      result = result.filter(u => u.email.toLowerCase().includes(filters.email.toLowerCase()));
    }

    setFilteredUsers(result);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingUser(null);
    fetchUsers();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await api.delete(`users/${id}/`);
        fetchUsers();
      } catch (error) {
        console.error("Error deleting user", error);
        alert('Erro ao excluir usuário.');
      }
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>Usuários</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Gerencie o acesso ao sistema</p>
        </div>
        {!showForm && (
          <button className="btn" onClick={() => setShowForm(true)}>+ Novo Usuário</button>
        )}
      </div>

      {showForm ? (
        <div style={{ marginBottom: '30px' }}>
          <UserForm
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            userToEdit={editingUser}
          />
        </div>
      ) : (
        <TableFilter
          filters={[
            { key: 'username', label: 'Usuário' },
            { key: 'name', label: 'Nome' },
            { key: 'email', label: 'Email' }
          ]}
          onFilter={handleFilter}
        />
      )}

      <div className="card" style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Usuário</th>
              <th>Nome Completo</th>
              <th>Email</th>
              <th>Perfil de Acesso</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-light)' }}>
                  Nenhum usuário encontrado.
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td style={{ fontWeight: '500' }}>{user.username}</td>
                  <td>{user.first_name} {user.last_name}</td>
                  <td>{user.email}</td>
                  <td>
                    {user.groups && user.groups.length > 0 ? (
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        {user.groups.map((group, idx) => (
                          <span key={idx} className="badge" style={{ backgroundColor: 'var(--bg-hover)', color: 'var(--text-primary)' }}>
                            {group}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>Padrão</span>
                    )}
                  </td>
                  <td>
                    {user.is_active ? (
                      <span className="badge badge-success">Ativo</span>
                    ) : (
                      <span className="badge badge-danger">Inativo</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <ActionButtons
                      onEdit={() => handleEdit(user)}
                      onDelete={() => user.id && handleDelete(user.id)}
                      deletePermission="delete_user"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersPage;
